import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {t} from 'i18next';
import {Typography} from '@mui/material';
import {useDispatch, useSelector} from 'react-redux';
import _ from 'lodash';
import {useNavigate} from 'react-router-dom';
import AurStepper from '../../../common/aur-stepper-tab/AurStepperTab';
import ProjectInfoForm from '../../project-creation/CreateProject/component/ProjectInfoForm';
import {CreateProjectFormDataType} from './types/createProjectTypes';
import AurPopup from '../../../common/aur-popup/AurPopup';
import {defaultProjectFormData} from './utils/projectFormConfig';
import navConstants from '../../../../constants/navConstants';
import {RootState} from '../../../../redux/store';
import ReviewAndAcknoledgement from './component/ReviewAndAcknowledgement';
import HotLeadFormInfoBanner from './component/HotLeadFormInfoBanner';
import Confirmation from './component/Confirmation';
import LeverageGenAI from './component/LeverageGenAI';
import HeaderBackButton from '../components/HeaderBackButton';
import {
  PROJECT_CREATION_PATH,
  setProjectCreationInitiatedFrom,
} from '../../../../redux/slices/projectCreationSlice';
import {ReactComponent as GrayTickIcon} from '../../../../assests/svg/gray-tick.svg';
import {useHasClientPermission} from '../../../../hooks/permissions/usePermissionsByClientId';
import {CLIENT_PERMISSION_CODES_CONSTANTS} from '../../../../constants/constants';

enum PROJECT_CREATE_STEPS {
  INFO = 0,
  LEVERAGE_GENAI = 1,
  REVIEW = 2,
  CONFIRMATION = 3,
}

const stringPath = 'projectCreation.creation.';

const CreateProject = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const userId = useSelector((state: RootState) => state.user.user)?.userId;
  const {clientId} = useSelector((state: RootState) => state.projectCreation);

  const hasCreateProjectPermission = useHasClientPermission(
    clientId ?? '',
    CLIENT_PERMISSION_CODES_CONSTANTS.CREATE_NEW_PROJECT
  );

  const focusAnchorRef = useRef<HTMLDivElement>(null);

  const {selectedAgreement, projectCreationInitiatedFrom} = useSelector(
    (state: RootState) => state.projectCreation
  );
  const [activeStep, setActiveStep] = useState<PROJECT_CREATE_STEPS>(
    PROJECT_CREATE_STEPS.INFO
  );
  const [projectData, setProjectData] = useState<CreateProjectFormDataType>({
    ...defaultProjectFormData,
    serviceAgreement: selectedAgreement?.id ?? '',
  });
  const [isAIInvestigateEnabled, setIsAIInvestigateEnabled] =
    useState<boolean>(false);
  const [isCloseConfirmationModalVisible, setIsCloseConfirmationModalVisible] =
    useState<boolean>(false);
  const initialProjectData = useRef<CreateProjectFormDataType>({
    ...defaultProjectFormData,
    serviceAgreement: selectedAgreement?.id ?? '',
  });
  const showConfirmCloseForHotLeadFrom = useRef(false);

  useEffect(() => {
    if (!clientId) {
      navigate('/welcome-multi-client', {replace: true});
    } else if (!hasCreateProjectPermission) {
      navigate('/secure-page', {replace: true});
    } else if (!selectedAgreement) {
      navigate('/welcome-single-client', {replace: true});
    }
  }, [hasCreateProjectPermission]);

  const stepperSteps = useMemo(() => {
    const GrayTick = <GrayTickIcon aria-hidden={true} />;
    return [
      {
        label: t(stringPath + 'steps.projectInfo'),
        disabled: activeStep === PROJECT_CREATE_STEPS.CONFIRMATION,
        disableTickIcon: GrayTick,
      },
      {
        label: t(stringPath + 'steps.leverageGenAI'),
        disabled: activeStep === PROJECT_CREATE_STEPS.CONFIRMATION,
        disableTickIcon: GrayTick,
      },
      {
        label: t(stringPath + 'steps.review'),
        disabled: activeStep === PROJECT_CREATE_STEPS.CONFIRMATION,
        disableTickIcon: GrayTick,
      },
      {label: t(stringPath + 'steps.confirmation')},
    ];
  }, [activeStep]);

  const haveUnsavedChanges = useMemo(() => {
    if (!_.isEqual(projectData, initialProjectData.current)) {
      return true;
    }
    return false;
  }, [projectData]);

  const executeBack = useCallback(() => {
    switch (projectCreationInitiatedFrom) {
      case PROJECT_CREATION_PATH.ClientDetails:
        navigate('/clients/details', {replace: true});
        break;
      case PROJECT_CREATION_PATH.ClientOverview:
      default:
        navigate('/welcome-single-client', {replace: true});
        break;
    }
    dispatch(setProjectCreationInitiatedFrom(null));
  }, [projectCreationInitiatedFrom, navigate]);

  const onProjectInfoSave = useCallback(() => {
    setActiveStep(PROJECT_CREATE_STEPS.LEVERAGE_GENAI);
  }, []);

  const onReviewAndAckSave = useCallback(() => {
    setActiveStep(PROJECT_CREATE_STEPS.CONFIRMATION);
  }, []);

  const onEditProjectDetails = useCallback(() => {
    setActiveStep(PROJECT_CREATE_STEPS.INFO);
  }, []);

  const onAIInvestigationSave = useCallback(() => {
    setActiveStep(PROJECT_CREATE_STEPS.REVIEW);
  }, []);

  const onBackClick = useCallback(() => {
    if (haveUnsavedChanges) {
      setIsCloseConfirmationModalVisible(true);
    } else {
      executeBack();
    }
  }, [haveUnsavedChanges]);

  useEffect(() => {
    // when active step changes then focus order should start from back button
    focusAnchorRef.current?.focus();
  }, [activeStep]);

  const renderHeader = () => {
    return (
      <div className="header" ref={focusAnchorRef} tabIndex={-1}>
        <HeaderBackButton
          isHidden={activeStep === PROJECT_CREATE_STEPS.CONFIRMATION}
          onClick={onBackClick}
        />

        <Typography className="typo-text headline1_size title-text">
          {t(stringPath + 'title')}
        </Typography>
        <div>
          <AurStepper
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            steps={stepperSteps}
            keepOnlyActiveStepFocusable
          />
        </div>
      </div>
    );
  };

  const renderStepperContent = () => {
    switch (activeStep) {
      case PROJECT_CREATE_STEPS.INFO:
        return (
          <ProjectInfoForm
            onSaveNContinue={onProjectInfoSave}
            onFormDataInput={setProjectData}
            projectData={projectData}
            haveUnsavedChanges={haveUnsavedChanges}
            openHotLeadForm={() => {
              showConfirmCloseForHotLeadFrom.current = true;
              setIsCloseConfirmationModalVisible(true);
            }}
          />
        );
      case PROJECT_CREATE_STEPS.LEVERAGE_GENAI:
        return (
          <LeverageGenAI
            onSaveNContinue={onAIInvestigationSave}
            onAIToggleButton={setIsAIInvestigateEnabled}
            IsAIInvestigateEnabled={isAIInvestigateEnabled}
          />
        );
      case PROJECT_CREATE_STEPS.REVIEW:
        return (
          <ReviewAndAcknoledgement
            onSaveNContinue={onReviewAndAckSave}
            projectInfo={projectData}
            onEditDetails={onEditProjectDetails}
            genAIEnabled={isAIInvestigateEnabled}
            userId={userId ?? ''}
            clientId={clientId ?? ''}
            hotLeadBanner={
              <div className="review-container">
                <HotLeadFormInfoBanner
                  haveUnsavedChange={haveUnsavedChanges}
                  openHotLeadForm={() => {
                    showConfirmCloseForHotLeadFrom.current = true;
                    setIsCloseConfirmationModalVisible(true);
                  }}
                />{' '}
              </div>
            }
          />
        );
      case PROJECT_CREATE_STEPS.CONFIRMATION:
        return <Confirmation projectName={projectData.projectName ?? ''} />;
      default:
        return null;
    }
  };
  return (
    <div className="create-project-container">
      {renderHeader()}
      <div className="stepper-content-container">{renderStepperContent()}</div>
      <AurPopup
        open={isCloseConfirmationModalVisible}
        showPopupTitle
        showCloseIcon
        showPopupHeading
        contentType="text"
        popupTitle={t('common.justToConfirm')}
        popupHeading={t(stringPath + 'comfirmCloseModal.title')}
        popupContent={t(stringPath + 'comfirmCloseModal.message')}
        labelActionOk={t(stringPath + 'comfirmCloseModal.labelOk')}
        labelActionCancel={t(stringPath + 'comfirmCloseModal.labelCancel')}
        onOk={() => {
          if (showConfirmCloseForHotLeadFrom.current)
            navigate(navConstants.projectCreation.hotLeadsForm);
          else executeBack();
          showConfirmCloseForHotLeadFrom.current = false;
        }}
        onCancel={() => {
          setIsCloseConfirmationModalVisible(false);
          showConfirmCloseForHotLeadFrom.current = false;
        }}
        className="create-project-modal aur-unsaved-changes-popup"
      />
    </div>
  );
};

export default CreateProject;
